import type { FilterOption, FiltersAPIRawResponse } from './types'
import type { RoomsParticipantDistribution } from '@/domain/shared/types'

import type { FilterSelection, FiltersResponse } from '@/scopes/prices/domain/filters/types'
import { add, format, getDay, lastDayOfMonth, max, nextSunday, previousMonday, sub } from 'date-fns'
import { isEmpty } from 'ramda'
import { mapMonthValue } from '@/scopes/prices/domain/utils'

export interface MappedFiltersResponse extends FiltersResponse {
  filterSelection: FilterSelection
  isError?: boolean
  isEmpty?: boolean
}

export interface FiltersResponseMapper {
  map: (
    filtersResponse: FiltersAPIRawResponse,
    originalParticipants?: RoomsParticipantDistribution | undefined,
  ) => MappedFiltersResponse
}

export function createFiltersResponseMapper(): FiltersResponseMapper {
  const map = (
    filtersResponse: FiltersAPIRawResponse,
    originalParticipants: RoomsParticipantDistribution | undefined = undefined,
  ) => {
    const durationsOptions = mapDurations(filtersResponse)
    const mappedResponse = {
      DepartureAirport: {
        options:
          filtersResponse.departureAirports?.map(({ text, value, groupInfo, selected }) => ({
            name: text,
            value,
            groupInfo,
            selected,
          })) ?? [],
      },
      Duration: {
        options: durationsOptions,
      },
      Mealplan: {
        options: filtersResponse.mealplans?.map(({ text, value, selected }) => ({ name: text, value, selected })) ?? [],
      },
      Month: {
        options:
          filtersResponse.months?.map(({ text, value, selected }) => ({
            name: text,
            value: mapMonthValue(value),
            selected,
          })) ?? [],
      },
      TransportType: {
        options:
          filtersResponse.transportTypes?.map(({ text, value, selected }) => ({ name: text, value, selected })) ?? [],
      },
      RoomType: {
        options:
          filtersResponse.roomTypes?.map(({ text, value, count }) => ({ name: text, value, resultsCount: count }))
          ?? [],
      },
      Participants: [],
      filterSelection: {
        DepartureAirport: getSelectedValues(filtersResponse.departureAirports),
        Duration: getSelectedValues(durationsOptions),
        Mealplan: getSelectedValues(filtersResponse.mealplans).join(),
        Month: mapMonthValue(getSelectedMonth(filtersResponse)),
        TransportType: getSelectedValues(filtersResponse.transportTypes).join(),
        RoomType: getSelectedValues(filtersResponse.roomTypes),
        Participants: originalParticipants,
        DateFrom: getDateFrom(filtersResponse),
        DateTo: getDateTo(filtersResponse),
      },
    } as MappedFiltersResponse

    const isEmpty = checkIsEmptyResponse(mappedResponse)
    return {
      ...mappedResponse,
      isEmpty,
    }
  }

  function mapDurations(filtersResponse: FiltersAPIRawResponse) {
    const hasMetadata = (filtersResponse.durationsRanges?.filter(({ extra }) => extra && !isEmpty(extra))?.length ?? 0) === filtersResponse.durations.length
    if (!hasMetadata) {
      return filtersResponse.durations?.map(({ text, value, selected }) => ({ name: text, value, selected, extra: undefined })) ?? []
    }
    const textsToShow = filtersResponse.durationsRanges?.map(({ text }) => text) ?? []
    return filtersResponse.durations?.filter(({ text }) => textsToShow.includes(text))?.map(({ text, value, selected }) => ({ name: text, value, selected, extra: undefined })) ?? []
  }

  function getSelectedMonth(filtersResponse: FiltersAPIRawResponse) {
    return getSelectedValues(filtersResponse.months).join()
  }

  function getDateFrom(filtersResponse: FiltersAPIRawResponse) {
    if ((filtersResponse?.months?.length ?? 0) === 0)
      return undefined

    const selectedMonth = new Date(getSelectedMonth(filtersResponse))
    const maxDate = max([selectedMonth, new Date()])
    const prevMonday = getDay(maxDate) === 1 ? sub(maxDate, { days: 7 }) : previousMonday(maxDate)
    return format(prevMonday, 'yyyy-MM-dd')
  }

  function getDateTo(filtersResponse: FiltersAPIRawResponse) {
    if ((filtersResponse?.months?.length ?? 0) === 0)
      return undefined

    const selectedMonth = new Date(getSelectedMonth(filtersResponse))
    const lastDay = lastDayOfMonth(selectedMonth)
    const nextSundayOfLastDayOfMonth = getDay(lastDay) === 0 ? add(lastDay, { days: 7 }) : nextSunday(lastDay)
    return format(nextSundayOfLastDayOfMonth, 'yyyy-MM-dd')
  }

  function getSelectedValues(options: FilterOption[]) {
    return options?.filter(option => option.selected).map(option => option.value) ?? []
  }

  function checkIsEmptyResponse(filters?: Partial<MappedFiltersResponse>) {
    if (!filters)
      return true
    const keysToCheck: (keyof MappedFiltersResponse)[] = ['Duration', 'Mealplan', 'Month', 'TransportType']
    return keysToCheck.every((key) => {
      const filter = filters[key] as { options?: unknown[] }
      return Array.isArray(filter?.options) && filter.options.length === 0
    })
  }

  return { map }
}
